import React from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import { TabletButton } from '@campfire/tablet-button';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import {
  EMPTY_SEEK_OPPORTUNITY_SETTINGS,
  getMissingSeekFields,
  SeekOpportunityFields,
  SeekOpportunitySettings,
} from '../Integrations/Apps/Setups/SeekVolunteer/SeekOpportunityFields';
import { AdminConsoleFunnel } from './AdminConsoleFunnels';

type SeekDefaults = {
  userEmail: string | null;
  defaultCategoryId: number | null;
  defaultCommitmentId: number | null;
  defaultEmail: string | null;
  defaultLocations: string[];
  defaultIsOnlineOrRemote: boolean;
};

interface Props {
  funnel: AdminConsoleFunnel;
  onClose: () => void;
  onPublished: () => void;
  onError: (message: string) => void;
}

export function PublishToSeekDialog({ funnel, onClose, onPublished, onError }: Props) {
  const apiUrl = useApiUrl();
  const { theme } = useCampfireTheme();
  const defaultsFetch = useCampfireFetch<SeekDefaults>({ defer: true });
  const publishFetch = useCampfireFetch({ defer: true });

  const [isLoading, setIsLoading] = React.useState(true);
  const [isPublishing, setIsPublishing] = React.useState(false);
  const [settings, setSettings] = React.useState<SeekOpportunitySettings>(EMPTY_SEEK_OPPORTUNITY_SETTINGS);

  React.useEffect(() => {
    defaultsFetch
      .run({ url: `${apiUrl}/wm/integrations/seek-volunteer/manage/get`, method: 'get' })
      .then((response) => {
        const { data } = response.data;
        setSettings({
          categoryId: data.defaultCategoryId ?? null,
          commitmentId: data.defaultCommitmentId ?? null,
          email: data.defaultEmail ?? data.userEmail ?? '',
          locations: data.defaultLocations ?? [],
          isOnlineOrRemote: data.defaultIsOnlineOrRemote ?? false,
        });
      })
      .finally(() => setIsLoading(false));
  }, []);

  const missing = getMissingSeekFields(settings);

  const onPublish = () => {
    setIsPublishing(true);
    publishFetch
      .run({
        url: `${apiUrl}/wm/integrations/seek-volunteer/publish-funnel`,
        method: 'post',
        data: {
          funnelId: funnel.funnelId,
          funnelName: funnel.name,
          funnelDescription: funnel.description || '',
          categoryId: settings.categoryId,
          commitmentId: settings.commitmentId,
          email: settings.email.trim(),
          locations: settings.locations,
          isOnlineOrRemote: settings.isOnlineOrRemote,
        },
      })
      .then(onPublished)
      .catch((error: any) => onError(error?.response?.data?.message || 'Failed to publish to SEEK Volunteer'))
      .finally(() => setIsPublishing(false));
  };

  return (
    <Dialog open onClose={isPublishing ? undefined : onClose} fullWidth maxWidth='sm'>
      <DialogTitle>Publish to SEEK Volunteer</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box display='flex' justifyContent='center' py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box>
            <Typography variant='body2' style={{ color: '#808080', marginBottom: '1rem' }}>
              {`"${funnel.name}" will be published as an opportunity. Values are pre-filled from your SEEK Volunteer defaults.`}
            </Typography>
            <SeekOpportunityFields value={settings} onChange={setSettings} />
            {missing.length > 0 && (
              <Typography style={{ fontSize: '13px', color: theme.alert.red.light, marginTop: '0.75rem' }}>
                {`Required: ${missing.join(', ')}`}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <TabletButton onClick={onClose} disabled={isPublishing}>
          Cancel
        </TabletButton>
        <TabletButton
          onClick={onPublish}
          variant='contained'
          disabled={isLoading || isPublishing || missing.length > 0}
          style={{
            backgroundColor: theme.color.secondary.main900,
            color: '#ffffff',
            borderRadius: '4px',
            padding: '7px 12px',
            fontWeight: 600,
            fontSize: '13px',
            opacity: isLoading || isPublishing || missing.length > 0 ? 0.5 : 1,
          }}
        >
          {isPublishing ? <CircularProgress size={16} style={{ color: '#fff' }} /> : 'Publish'}
        </TabletButton>
      </DialogActions>
    </Dialog>
  );
}
